import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Chip,
  LinearProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Container,
  TextField,
  Snackbar,
} from '@mui/material';
import {
  People as PeopleIcon,
  AccessTime as AccessTimeIcon,
  EventAvailable as EventAvailableIcon,
  Payment as PaymentIcon,
  TrendingUp as TrendingUpIcon,
  Celebration as CelebrationIcon,
  Business as BusinessIcon,
  CreditCard as CreditCardIcon,
  Upgrade as UpgradeIcon,
  Settings as SettingsIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  AttachFile as PaperClipIcon,
  Description as DocumentTextIcon,
  Delete as TrashIcon,
  Cloud as CloudIcon,
  QrCode as QrCodeIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { useGetMyCompanyQuery } from '../../services/companyApi';
import { useGetMySubscriptionQuery, useUpgradeSubscriptionMutation } from '../../services/subscriptionApi';
import { useGetPlansQuery } from '../../services/planApi';
import { useGetEmployeesQuery } from '../../services/employeeApi';
import { useGetPublicBankDetailsQuery } from '../../store/api/publicApi';
import StorageMonitoringWidget from '../../components/StorageMonitoringWidget';

// Import EmployeePortal component for employee users
import EmployeePortal from '../EmployeePortal';

const Dashboard = () => {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');
  const [billingCycle, setBillingCycle] = useState('yearly'); // Default to yearly
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [currentUpgradeStep, setCurrentUpgradeStep] = useState(1);
  const [upgradeData, setUpgradeData] = useState({
    planId: '',
    billingCycle: 'yearly',
    paymentMethod: 'bank_transfer',
    paymentReference: '',
    paymentAttachments: []
  });
  const [upgradeErrors, setUpgradeErrors] = useState({});
  const [showQRModal, setShowQRModal] = useState(false);

  // Fetch bank details for payment
  const { data: bankData, isLoading: bankLoading } = useGetPublicBankDetailsQuery();
  const bankDetails = bankData?.data || {};

  console.log('Dashboard component rendering');
  console.log('User from Redux:', user);
  console.log('User role check:', user?.role);
  console.log('Is company_admin?', user?.role === 'company_admin');

  // Fetch company and subscription data
  const { data: companyData, isLoading: companyLoading, error: companyError } = useGetMyCompanyQuery();
  const { data: subscriptionData, isLoading: subscriptionLoading, error: subscriptionError } = useGetMySubscriptionQuery();
  const { data: plansData, isLoading: plansLoading } = useGetPlansQuery(companyData?.data?.countryCode || 'US');
  const { data: employeesData, isLoading: employeesLoading } = useGetEmployeesQuery();
  const [upgradeSubscription, { isLoading: upgrading }] = useUpgradeSubscriptionMutation();

  console.log('API Queries initialized');
  console.log('Company query state:', { data: companyData, loading: companyLoading, error: companyError });
  console.log('Subscription query state:', { data: subscriptionData, loading: subscriptionLoading, error: subscriptionError });
  console.log('Plans query state:', { data: plansData, loading: plansLoading });

  const company = companyData?.data;
  const subscription = subscriptionData?.data;
  const plans = plansData || [];
  const employees = employeesData?.data?.employees || [];

  console.log('Employee data structure:', employeesData);
  console.log('Employees array:', employees);
  console.log('Employees count:', employees.length);

  // Helper function to format date as dd/mm/yyyy
  const formatDate = (dateString) => {
    if (!dateString || dateString === 'N/A' || dateString === null || dateString === undefined) return 'N/A';
    try {
      const date = new Date(dateString);
      // Check if date is valid and not the Unix epoch (which would be created from null)
      if (isNaN(date.getTime()) || date.getTime() === 0) return 'N/A';
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      return 'N/A';
    }
  };

  console.log('Dashboard data:', { company, subscription, user });
  console.log('companyLoading:', companyLoading, 'subscriptionLoading:', subscriptionLoading);
  console.log('User role:', user?.role);
  console.log('User ID:', user?._id);
  console.log('Company data:', companyData);
  console.log('Subscription data:', subscriptionData);
  console.log('Company error:', companyError);
  console.log('Subscription error:', subscriptionError);

  // Check user roles
  const isCompanyAdmin = user?.role === 'company_admin' || user?.role === 'admin';
  const isEmployee = user?.role === 'employee' || user?.role === 'user';
  const isSuperAdmin = user?.role === 'super_admin';

  console.log('isCompanyAdmin:', isCompanyAdmin);
  console.log('isEmployee:', isEmployee);
  console.log('isSuperAdmin:', isSuperAdmin);
  console.log('User role:', user?.role);

  // Redirect super admin to super admin dashboard
  useEffect(() => {
    if (isSuperAdmin) {
      navigate('/super-admin/dashboard');
    }
  }, [isSuperAdmin, navigate]);

  // Calculate trial days left
  const getTrialDaysLeft = () => {
    if (!subscription?.trialEndDate) return 14;
    const daysLeft = Math.max(0, Math.ceil((new Date(subscription.trialEndDate) - new Date()) / (1000 * 60 * 60 * 24)));
    return daysLeft;
  };

  // Calculate past due days
  const getPastDueDays = () => {
    if (!subscription?.nextBillingDate) return 0;
    const daysPastDue = Math.max(0, Math.ceil((new Date() - new Date(subscription.nextBillingDate)) / (1000 * 60 * 60 * 24)));
    return daysPastDue;
  };

  // Handle upgrade dialog
  const handleUpgradeClick = () => {
    setCurrentUpgradeStep(1);
    setUpgradeData({
      planId: '',
      billingCycle: 'yearly',
      paymentMethod: 'bank_transfer',
      paymentReference: '',
      paymentAttachments: []
    });
    setUpgradeErrors({});
    setUpgradeDialogOpen(true);
  };

  const handleUpgradeNext = () => {
    if (validateUpgradeStep(currentUpgradeStep)) {
      setCurrentUpgradeStep(prev => prev + 1);
    }
  };

  const handleUpgradePrevious = () => {
    setCurrentUpgradeStep(prev => prev - 1);
  };

  const validateUpgradeStep = (step = currentUpgradeStep) => {
    const errors = {};
    
    if (step === 1) {
      if (!upgradeData.planId) {
        errors.planId = 'Please select a plan';
      }
    } else if (step === 2) {
      if (!upgradeData.paymentMethod) {
        errors.paymentMethod = 'Please select a payment method';
      }
      if (!upgradeData.paymentReference?.trim()) {
        errors.paymentReference = 'Payment reference is required';
      }
    }

    setUpgradeErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleUpgradeConfirm = async () => {
    if (!validateUpgradeStep(2)) {
      return;
    }

    if (!subscription?._id || !upgradeData.planId) return;

    setIsUpgrading(true);
    try {
      const result = await upgradeSubscription({
        id: subscription._id,
        planId: upgradeData.planId,
        billingCycle: upgradeData.billingCycle,
        paymentMethod: upgradeData.paymentMethod,
        paymentReference: upgradeData.paymentReference,
        paymentAttachments: upgradeData.paymentAttachments
      }).unwrap();
      
      console.log('Upgrade successful:', result);
      setUpgradeDialogOpen(false);
      setCurrentUpgradeStep(1);
      setSelectedPlan('');
      setBillingCycle('yearly');
      setUpgradeData({
        planId: '',
        billingCycle: 'yearly',
        paymentMethod: 'bank_transfer',
        paymentReference: '',
        paymentAttachments: []
      });
      setUpgradeErrors({});
      // The subscription data will be automatically refetched due to invalidation
    } catch (error) {
      console.error('Upgrade failed:', error);
      setUpgradeErrors({ general: 'Upgrade failed. Please try again.' });
    } finally {
      setIsUpgrading(false);
    }
  };

  const handleUpgradeCancel = () => {
    setUpgradeDialogOpen(false);
    setCurrentUpgradeStep(1);
    setSelectedPlan('');
    setBillingCycle('yearly');
    setUpgradeData({
      planId: '',
      billingCycle: 'yearly',
      paymentMethod: 'bank_transfer',
      paymentReference: '',
      paymentAttachments: []
    });
    setUpgradeErrors({});
  };

  function capitalizeFirst(str) {
    if (!str) return "";
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  // Helper functions for upgrade modal
  const handleUpgradeDataUpdate = (data) => {
    setUpgradeData(prev => ({ ...prev, ...data }));
    if (data.planId) {
      setSelectedPlan(data.planId);
    }
    if (data.billingCycle) {
      setBillingCycle(data.billingCycle);
    }
  };

  const handleFileUpload = async (event) => {
    const files = Array.from(event.target.files);
    const maxFileSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];

    const validFiles = files.filter(file => {
      if (file.size > maxFileSize) {
        console.error(`File ${file.name} is too large. Maximum size is 5MB.`);
        return false;
      }
      if (!allowedTypes.includes(file.type)) {
        console.error(`File ${file.name} is not supported. Please use JPG, PNG, or PDF files.`);
        return false;
      }
      return true;
    });

    if (validFiles.length > 0) {
      try {
        const newAttachments = await Promise.all(
          validFiles.map(file => {
            return new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = (e) => {
                resolve({
                  id: Date.now() + Math.random(),
                  name: file.name,
                  size: file.size,
                  type: file.type,
                  data: e.target.result, // Base64 data
                  uploadedAt: new Date().toISOString()
                });
              };
              reader.onerror = reject;
              reader.readAsDataURL(file);
            });
          })
        );

        const updatedAttachments = [...upgradeData.paymentAttachments, ...newAttachments];
        handleUpgradeDataUpdate({ paymentAttachments: updatedAttachments });
      } catch (error) {
        console.error('File upload error:', error);
      }
    }
  };

  const removeAttachment = (attachmentId) => {
    const updatedAttachments = upgradeData.paymentAttachments.filter(att => att.id !== attachmentId);
    handleUpgradeDataUpdate({ paymentAttachments: updatedAttachments });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getCurrencySymbol = (curr) => {
    const symbols = {
      'USD': '$', 'EUR': '€', 'GBP': '£', 'CAD': 'C$', 'AUD': 'A$',
      'INR': '₹', 'SGD': 'S$', 'AED': 'د.إ', 'BRL': 'R$', 'MXN': '$',
      'ZAR': 'R', 'NGN': '₦', 'KES': 'KSh', 'EGP': '£'
    };
    return symbols[curr] || '$';
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      // Could add a toast notification here if you have a toast system
      console.log('Copied to clipboard:', text);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };  const stats = [
    {
      title: 'Total Employees',
      value: employeesLoading ? 'Loading...' : (Array.isArray(employees) ? employees.length.toString() : '0'),
      change: employeesLoading ? 'Loading...' : `${Array.isArray(employees) ? employees.length : 0} employees`,
      icon: PeopleIcon,
      color: '#1976d2',
    },
    {
      title: 'Company Status',
      value: company ? 'Active' : 'Setting up',
      change: company?.name || 'Loading...',
      icon: BusinessIcon,
      color: '#2e7d32',
    },
    {
      title: 'Subscription',
      value: capitalizeFirst(subscription?.status) || 'Trial',
      change: subscription?.planId?.name || 'Starter',
      icon: CreditCardIcon,
      color: subscription?.status === 'trial' ? '#f57c00' : '#2e7d32',
    },
    {
      title: isCompanyAdmin && subscription?.status === 'trial' ? 'Trial Days Left' : 'Next Bill',
      value: isCompanyAdmin && subscription?.status === 'trial' ? 
        getTrialDaysLeft() : 
        (subscription?.nextBillingDate ? formatDate(subscription.nextBillingDate) : 'N/A'),
      change: isCompanyAdmin && subscription?.status === 'trial' ? 'days remaining' : 'Next payment',
      icon: PaymentIcon,
      color: isCompanyAdmin && subscription?.status === 'trial' && getTrialDaysLeft() <= 3 ? '#d32f2f' : '#f57c00',
    },
  ];

  const setupGuides = [
    {
      id: 1,
      title: '🏢 Company Settings',
      description: 'Configure your company profile, contact details, and basic information',
      steps: [
        'Update company name, address, and contact information',
        'Configure company logo and branding',
        'Set timezone and regional settings',
        'Configure notification preferences'
      ],
      icon: 'business',
      color: '#1976d2',
      path: '/settings'
    },
    {
      id: 2,
      title: '� Master Data Setup',
      description: 'Set up organizational structure with departments, designations, branches, and shifts',
      steps: [
        'Create departments and organizational hierarchy',
        'Add job designations and roles',
        'Configure branch locations and details',
        'Set up work shifts and schedules'
      ],
      icon: 'account_tree',
      color: '#2e7d32',
      path: '/master'
    },
    {
      id: 3,
      title: '💼 Tax & Salary Configuration',
      description: 'Configure tax settings and salary structures for payroll processing',
      steps: [
        'Set up tax master configurations and rates',
        'Configure salary components and allowances',
        'Define salary structures for different roles',
        'Set up tax deduction rules and calculations'
      ],
      icon: 'calculate',
      color: '#f57c00',
      path: '/tax-master'
    },
    {
      id: 4,
      title: '👥 Employee Management',
      description: 'Add employees, assign roles, and manage organizational structure',
      steps: [
        'Create employee profiles with personal details',
        'Assign departments, designations, and reporting managers',
        'Configure salary structures and compensation',
        'Set up access permissions and user roles'
      ],
      icon: 'people',
      color: '#9c27b0',
      path: '/employees'
    },
    {
      id: 5,
      title: '⏰ Attendance & Leave Setup',
      description: 'Configure attendance tracking, leave policies, and holiday calendar',
      steps: [
        'Set up attendance tracking and shift assignments',
        'Configure leave types and allocation policies',
        'Set up holiday calendar and public holidays',
        'Configure approval workflows and hierarchies'
      ],
      icon: 'schedule',
      color: '#d32f2f',
      path: '/attendance'
    },
    {
      id: 6,
      title: '💰 Payroll Processing',
      description: 'Set up payroll cycles, calculations, and automated processing',
      steps: [
        'Configure payroll cycles and processing schedules',
        'Set up automated payroll calculations',
        'Configure salary slip generation and distribution',
        'Enable payroll reports and analytics'
      ],
      icon: 'payment',
      color: '#0288d1',
      path: '/payroll'
    },
    {
      id: 7,
      title: '📊 Reports & Analytics',
      description: 'Configure reporting dashboards and automated insights',
      steps: [
        'Set up payroll summary and employee reports',
        'Configure PF, ESI, and PT reports',
        'Enable TDS reports and tax filings',
        'Set up automated report generation and notifications'
      ],
      icon: 'analytics',
      color: '#7b1fa2',
      path: '/reports'
    }
  ];

  // Dynamic task generation based on company state
  const generateUpcomingTasks = () => {
    const tasks = [];
    const today = new Date();
    const currentUser = user;

    // Past due payment warning (highest priority)
    if (isCompanyAdmin && subscription?.status === 'past_due') {
      tasks.push({
        id: 'past_due_payment',
        title: 'Overdue Subscription Payment',
        date: subscription?.nextBillingDate ? formatDate(subscription.nextBillingDate) : 'Immediate',
        type: 'urgent',
        priority: 0, // Higher than trial warning
        action: () => setUpgradeDialogOpen(true),
        description: `Your subscription payment is ${getPastDueDays()} days overdue. Pay immediately to restore access.`,
        icon: '🚨'
      });
    }

    // Trial ending warning (highest priority)
    if (isCompanyAdmin && subscription?.status === 'trial' && getTrialDaysLeft() <= 7) {
      tasks.push({
        id: 'trial_warning',
        title: 'Trial Ending Soon',
        date: subscription?.trialEndDate ? formatDate(subscription.trialEndDate) : 'Soon',
        type: 'urgent',
        priority: 1,
        action: () => setUpgradeDialogOpen(true),
        description: `Your trial ends in ${getTrialDaysLeft()} days. Upgrade to continue using all features.`,
        icon: '⚠️'
      });
    }

    // Company setup completion check
    if (!company?.name || !company?.address?.street) {
      tasks.push({
        id: 'company_setup',
        title: 'Complete Company Profile',
        date: 'Today',
        type: 'setup',
        priority: 2,
        action: () => window.location.href = '/settings',
        description: 'Update your company information, address, and contact details.',
        icon: '🏢'
      });
    }

    // Employee management tasks
    if (employees.length === 0) {
      tasks.push({
        id: 'first_employee',
        title: 'Add Your First Employee',
        date: 'This week',
        type: 'onboarding',
        priority: 3,
        action: () => window.location.href = '/employees/add',
        description: 'Start building your team by adding employee profiles.',
        icon: '👤'
      });
    } else if (employees.length < 5) {
      tasks.push({
        id: 'more_employees',
        title: `Add ${5 - employees.length} More Employees`,
        date: 'This week',
        type: 'onboarding',
        priority: 4,
        action: () => window.location.href = '/employees/add',
        description: 'Continue building your team to maximize HRMS benefits.',
        icon: '👥'
      });
    }

    // Master data setup check
    if (!company?.departments?.length || company.departments.length === 0) {
      tasks.push({
        id: 'master_data',
        title: 'Set Up Organizational Structure',
        date: 'This week',
        type: 'setup',
        priority: 5,
        action: () => window.location.href = '/master',
        description: 'Configure departments, designations, branches, and shifts.',
        icon: '🏗️'
      });
    }

    // Payroll configuration check
    if (!company?.payrollConfigured) {
      tasks.push({
        id: 'payroll_setup',
        title: 'Configure Payroll System',
        date: 'Next week',
        type: 'setup',
        priority: 6,
        action: () => window.location.href = '/payroll',
        description: 'Set up payroll cycles, tax configurations, and salary structures.',
        icon: '💰'
      });
    }

    // Attendance system setup
    if (!company?.attendanceConfigured) {
      tasks.push({
        id: 'attendance_setup',
        title: 'Enable Attendance Tracking',
        date: 'Next week',
        type: 'setup',
        priority: 7,
        action: () => window.location.href = '/attendance',
        description: 'Configure attendance policies and shift management.',
        icon: '⏰'
      });
    }

    // Leave policies setup
    if (!company?.leavePoliciesConfigured) {
      tasks.push({
        id: 'leave_setup',
        title: 'Configure Leave Policies',
        date: 'Next week',
        type: 'setup',
        priority: 8,
        action: () => window.location.href = '/leave',
        description: 'Set up leave types, allocation rules, and approval workflows.',
        icon: '🏖️'
      });
    }

    // Subscription/billing reminders
    if (isCompanyAdmin && subscription?.status === 'active') {
      const nextBilling = subscription?.nextBillingDate ? new Date(subscription.nextBillingDate) : null;
      if (nextBilling && nextBilling.getTime() - today.getTime() <= 7 * 24 * 60 * 60 * 1000) {
        tasks.push({
          id: 'billing_reminder',
          title: 'Upcoming Bill Payment',
          date: formatDate(subscription.nextBillingDate),
          type: 'subscription',
          priority: 9,
          action: () => setUpgradeDialogOpen(true),
          description: `Your next billing cycle starts on ${formatDate(subscription.nextBillingDate)}.`,
          icon: '💳'
        });
      }
    }

    // Recent activity based tasks
    if (employees.length > 0) {
      const recentJoiners = employees.filter(emp => {
        const joinDate = new Date(emp.joiningDate);
        const daysSinceJoin = (today.getTime() - joinDate.getTime()) / (1000 * 60 * 60 * 24);
        return daysSinceJoin <= 30 && daysSinceJoin >= 0; // Employees joined in last 30 days
      });

      if (recentJoiners.length > 0) {
        tasks.push({
          id: 'welcome_new_employees',
          title: `Welcome ${recentJoiners.length} New Employee${recentJoiners.length > 1 ? 's' : ''}`,
          date: 'This week',
          type: 'onboarding',
          priority: 10,
          action: () => window.location.href = '/employees',
          description: 'Complete onboarding process for recently joined employees.',
          icon: '🎉'
        });
      }
    }

    // Monthly payroll reminder (if payroll is configured)
    if (company?.payrollConfigured && isCompanyAdmin) {
      const currentMonth = today.getMonth();
      const currentYear = today.getFullYear();
      const payrollDueDate = new Date(currentYear, currentMonth + 1, 1); // 1st of next month

      if (payrollDueDate.getTime() - today.getTime() <= 7 * 24 * 60 * 60 * 1000) {
        tasks.push({
          id: 'monthly_payroll',
          title: 'Monthly Payroll Processing',
          date: formatDate(payrollDueDate),
          type: 'payroll',
          priority: 11,
          action: () => window.location.href = '/payroll',
          description: 'Process monthly payroll and generate salary slips.',
          icon: '📊'
        });
      }
    }

    // Sort tasks by priority and return top 6
    return tasks
      .sort((a, b) => a.priority - b.priority)
      .slice(0, 6);
  };

  const upcomingEvents = generateUpcomingTasks();

  const getStatusColor = (type) => {
    const colors = {
      leave: '#ed6c02',
      employee: '#1976d2',
      payroll: '#9c27b0',
      attendance: '#2e7d32',
    };
    return colors[type] || '#666';
  };

  return (
    <>
      <Helmet>
        <title>Dashboard - {company?.name || 'IAR HRMS'}</title>
        <meta name="description" content="HRMS Dashboard with key metrics and insights" />
      </Helmet>

      {/* For Employee users, render EmployeePortal content */}
      {isEmployee ? (
        <EmployeePortal />
      ) : (
        /* For Admin/HR users, render the original Dashboard content */
        <Box sx={{ flexGrow: 1, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
          {/* Hero Section */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              py: 6,
              px: 3,
              mb: 4,
              position: 'relative',
              overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            },
          }}
        >
          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography
                  variant="h3"
                  component="h1"
                  sx={{
                    fontWeight: 800,
                    mb: 2,
                    fontSize: { xs: '2rem', md: '2.5rem' },
                  }}
                >
                  Welcome back, {user?.firstName}! 👋
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 400,
                    mb: 3,
                    opacity: 0.9,
                    fontSize: { xs: '1.1rem', md: '1.25rem' },
                  }}
                >
                  {isCompanyAdmin
                    ? `Here's what's happening with ${company?.name || 'your organization'}`
                    : 'Here\'s your personal dashboard overview'
                  }
                </Typography>

                {/* Quick Stats in Hero */}
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mt: 3 }}>
                  {isCompanyAdmin ? (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BusinessIcon sx={{ fontSize: 20 }} />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {company?.name || 'Company'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CreditCardIcon sx={{ fontSize: 20 }} />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {subscription?.status || 'Trial'} Plan
                        </Typography>
                      </Box>
                      {subscription?.status === 'trial' && (
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          backgroundColor: 'rgba(255, 255, 255, 0.15)',
                          padding: '8px 16px',
                          borderRadius: 2,
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          backdropFilter: 'blur(10px)'
                        }}>
                          <CelebrationIcon sx={{
                            fontSize: 20,
                            color: getTrialDaysLeft() <= 3 ? '#ffeb3b' : 'white'
                          }} />
                          <Typography variant="body2" sx={{
                            fontWeight: 600,
                            color: getTrialDaysLeft() <= 3 ? '#ffeb3b' : 'white'
                          }}>
                            {getTrialDaysLeft()} days left • Expires {formatDate(subscription.trialEndDate)}
                          </Typography>
                        </Box>
                      )}

                      {subscription?.status === 'past_due' && (
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          backgroundColor: 'rgba(244, 67, 54, 0.2)',
                          padding: '8px 16px',
                          borderRadius: 2,
                          border: '1px solid rgba(244, 67, 54, 0.4)',
                          backdropFilter: 'blur(10px)'
                        }}>
                          <PaymentIcon sx={{
                            fontSize: 20,
                            color: '#ffebee'
                          }} />
                          <Typography variant="body2" sx={{
                            fontWeight: 600,
                            color: '#ffebee'
                          }}>
                            Payment Overdue • {getPastDueDays()} days past due
                          </Typography>
                        </Box>
                      )}
                    </>
                  ) : (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon sx={{ fontSize: 20 }} />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {user?.designation || 'Employee'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BusinessIcon sx={{ fontSize: 20 }} />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {company?.name || 'Company'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccessTimeIcon sx={{ fontSize: 20 }} />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Employee Portal
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {isCompanyAdmin && subscription?.status === 'trial' && (
                    <Card
                      sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.15)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: 3,
                        p: 3,
                        textAlign: 'center',
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                        🚀 Ready to Upgrade?
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 3, opacity: 0.9 }}>
                        Unlock all features and grow your business
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<UpgradeIcon />}
                        onClick={handleUpgradeClick}
                        sx={{
                          backgroundColor: 'white',
                          color: '#667eea',
                          fontWeight: 600,
                          borderRadius: 2,
                          px: 3,
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.9)',
                            transform: 'translateY(-2px)',
                          },
                          transition: 'all 0.3s ease-in-out',
                        }}
                      >
                        Upgrade Now
                      </Button>
                    </Card>
                  )}

                  {isCompanyAdmin && (
                    <Card
                      sx={{
                        backgroundColor: 'rgba(255, 255, 255, 0.15)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: 3,
                        p: 3,
                        textAlign: 'center',
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                        ⚙️ Company Settings
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 3, opacity: 0.9 }}>
                        Manage company information and preferences
                      </Typography>
                      <Button
                        variant="outlined"
                        startIcon={<SettingsIcon />}
                        onClick={() => window.location.href = '/settings'}
                        sx={{
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                          color: 'white',
                          fontWeight: 600,
                          borderRadius: 2,
                          px: 3,
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            borderColor: 'rgba(255, 255, 255, 0.5)',
                            transform: 'translateY(-2px)',
                          },
                          transition: 'all 0.3s ease-in-out',
                        }}
                      >
                        Manage Settings
                      </Button>
                    </Card>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Trial Information Banner - Only for Company Admin in Trial */}
        {isCompanyAdmin && subscription?.status === 'trial' && (
          <Container maxWidth="lg" sx={{ px: 3, mb: 4 }}>
            <Card
              sx={{
                borderRadius: 4,
                background: getTrialDaysLeft() <= 3 
                  ? 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)'
                  : getTrialDaysLeft() <= 7
                  ? 'linear-gradient(135deg, #ffa726 0%, #fb8c00 100%)'
                  : 'linear-gradient(135deg, #4fc3f7 0%, #29b6f6 100%)',
                color: 'white',
                boxShadow: getTrialDaysLeft() <= 3
                  ? '0 8px 32px rgba(255, 107, 107, 0.3)'
                  : getTrialDaysLeft() <= 7
                  ? '0 8px 32px rgba(255, 167, 38, 0.3)'
                  : '0 8px 32px rgba(79, 195, 247, 0.3)',
                border: 'none',
                position: 'relative',
                overflow: 'hidden',
                ...(getTrialDaysLeft() <= 3 && {
                  animation: 'pulse 2s infinite',
                  '@keyframes pulse': {
                    '0%': { transform: 'scale(1)' },
                    '50%': { transform: 'scale(1.02)' },
                    '100%': { transform: 'scale(1)' },
                  },
                }),
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '200px',
                  height: '200px',
                  background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
                  borderRadius: '50%',
                  transform: 'translate(50px, -50px)',
                },
              }}
            >
              <CardContent sx={{ p: 4, position: 'relative', zIndex: 1 }}>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} md={8}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: 'rgba(255, 255, 255, 0.2)',
                          width: 56,
                          height: 56,
                          backdropFilter: 'blur(10px)',
                        }}
                      >
                        <CelebrationIcon sx={{ fontSize: 28, color: 'white' }} />
                      </Avatar>
                      <Box>
                        <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
                          {getTrialDaysLeft() <= 3 
                            ? '🚨 Trial Expiring Soon!' 
                            : getTrialDaysLeft() <= 7
                            ? '⏰ Trial Ending Soon'
                            : '🎉 Free Trial Active'
                          }
                        </Typography>
                        <Typography variant="body1" sx={{ opacity: 0.9, fontWeight: 500 }}>
                          Your {subscription.planId?.name || 'trial'} plan trial period
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Grid container spacing={3}>
                      <Grid item xs={6} sm={4}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h3" sx={{ 
                            fontWeight: 900, 
                            fontSize: '2.5rem',
                            color: getTrialDaysLeft() <= 3 ? '#ffeb3b' : 'white'
                          }}>
                            {getTrialDaysLeft()}
                          </Typography>
                          <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 600 }}>
                            Days Left
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                            {formatDate(subscription.trialEndDate)}
                          </Typography>
                          <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 600 }}>
                            Expiry Date
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                            {company?.currency || 'USD'} {subscription.price}
                          </Typography>
                          <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 600 }}>
                            After Trial ({subscription.billingCycle})
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body1" sx={{ mb: 3, opacity: 0.9, fontWeight: 500 }}>
                        {getTrialDaysLeft() <= 3
                          ? 'Don\'t lose access to your data! Upgrade now to continue.'
                          : getTrialDaysLeft() <= 7
                          ? 'Your trial is ending soon. Upgrade to keep all features.'
                          : 'Enjoying your trial? Upgrade anytime to unlock full potential.'
                        }
                      </Typography>
                      <Button
                        variant="contained"
                        size="large"
                        startIcon={<UpgradeIcon />}
                        onClick={handleUpgradeClick}
                        sx={{
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          color: 'white',
                          border: '2px solid rgba(255, 255, 255, 0.3)',
                          borderRadius: 3,
                          fontWeight: 700,
                          textTransform: 'none',
                          fontSize: '1.1rem',
                          py: 1.5,
                          px: 4,
                          backdropFilter: 'blur(10px)',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.3)',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.2)',
                          },
                          transition: 'all 0.3s ease-in-out',
                        }}
                      >
                        {getTrialDaysLeft() <= 3 ? '🚨 Upgrade Now' : '🚀 Upgrade Plan'}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Container>
        )}

        {/* Past Due Expiring Warning - Only show if past due */}
        {console.log('Rendering past due warning check:', { isCompanyAdmin, subscriptionStatus: subscription?.status, pastDueDays: getPastDueDays() })}
        {isCompanyAdmin && subscription?.status === 'past_due' && (
          <Container maxWidth="lg" sx={{ px: 3, mb: 3 }}>
            <Alert
              severity="error"
              sx={{
                borderRadius: 4,
                background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
                color: 'white',
                '& .MuiAlert-icon': { color: 'white' },
                boxShadow: '0 8px 32px rgba(244, 67, 54, 0.3)',
                border: 'none',
                animation: 'pulse 2s infinite',
                '@keyframes pulse': {
                  '0%': { transform: 'scale(1)', boxShadow: '0 8px 32px rgba(244, 67, 54, 0.3)' },
                  '50%': { transform: 'scale(1.02)', boxShadow: '0 12px 40px rgba(244, 67, 54, 0.4)' },
                  '100%': { transform: 'scale(1)', boxShadow: '0 8px 32px rgba(244, 67, 54, 0.3)' },
                },
              }}
              icon={<PaymentIcon />}
              action={
                <Button
                  color="inherit"
                  variant="contained"
                  startIcon={<UpgradeIcon />}
                  onClick={handleUpgradeClick}
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    borderRadius: 2,
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.3)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease-in-out',
                  }}
                >
                  💰 PAY NOW
                </Button>
              }
            >
              <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                ⚠️ PAYMENT OVERDUE: Your subscription payment is {getPastDueDays()} days past due. Service interruption imminent.
              </Typography>
            </Alert>
          </Container>
        )}

        {/* Past Due Information Banner - Only for Company Admin in Past Due */}
        {isCompanyAdmin && subscription?.status === 'past_due' && (
          <Container maxWidth="lg" sx={{ px: 3, mb: 4 }}>
            <Card
              sx={{
                borderRadius: 4,
                background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
                color: 'white',
                boxShadow: '0 8px 32px rgba(244, 67, 54, 0.3)',
                border: 'none',
                position: 'relative',
                overflow: 'hidden',
                animation: 'pulse 2s infinite',
                '@keyframes pulse': {
                  '0%': { transform: 'scale(1)' },
                  '50%': { transform: 'scale(1.02)' },
                  '100%': { transform: 'scale(1)' },
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '200px',
                  height: '200px',
                  background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
                  borderRadius: '50%',
                  transform: 'translate(50px, -50px)',
                },
              }}
            >
              <CardContent sx={{ p: 4, position: 'relative', zIndex: 1 }}>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} md={8}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar
                        sx={{
                          bgcolor: 'rgba(255, 255, 255, 0.2)',
                          width: 56,
                          height: 56,
                          backdropFilter: 'blur(10px)',
                        }}
                      >
                        <PaymentIcon sx={{ fontSize: 28, color: 'white' }} />
                      </Avatar>
                      <Box>
                        <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
                          🚨 Payment Overdue!
                        </Typography>
                        <Typography variant="body1" sx={{ opacity: 0.9, fontWeight: 500 }}>
                          Your {subscription.planId?.name || 'subscription'} plan payment is overdue
                        </Typography>
                      </Box>
                    </Box>

                    <Grid container spacing={3}>
                      <Grid item xs={6} sm={4}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h3" sx={{
                            fontWeight: 900,
                            fontSize: '2.5rem',
                            color: '#ffebee'
                          }}>
                            {getPastDueDays()}
                          </Typography>
                          <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 600 }}>
                            Days Overdue
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} sm={4}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                            {company?.currency || 'USD'} {subscription.price}
                          </Typography>
                          <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 600 }}>
                            Amount Due ({subscription.billingCycle})
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                            {formatDate(subscription.nextBillingDate)}
                          </Typography>
                          <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 600 }}>
                            Due Date
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body1" sx={{ mb: 3, opacity: 0.9, fontWeight: 500 }}>
                        Your subscription features are at risk. Pay now to restore full access and avoid service interruption.
                      </Typography>
                      <Button
                        variant="contained"
                        size="large"
                        startIcon={<UpgradeIcon />}
                        onClick={handleUpgradeClick}
                        sx={{
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          color: 'white',
                          border: '2px solid rgba(255, 255, 255, 0.3)',
                          borderRadius: 3,
                          fontWeight: 700,
                          textTransform: 'none',
                          fontSize: '1.1rem',
                          py: 1.5,
                          px: 4,
                          backdropFilter: 'blur(10px)',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.3)',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 25px rgba(0, 0, 0, 0.2)',
                          },
                          transition: 'all 0.3s ease-in-out',
                        }}
                      >
                        💰 Pay Now
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Container>
        )}

        {/* Enhanced Company & Subscription Cards */}
        <Container maxWidth="lg" sx={{ px: 3, mb: 4 }}>
          <Grid container spacing={3}>
            {/* Company Info Card */}
            {isCompanyAdmin && company && (
              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    borderRadius: 4,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
                    border: 'none',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: '150px',
                      height: '150px',
                      background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
                      borderRadius: '50%',
                      transform: 'translate(50px, -50px)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 4, position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <Avatar
                        sx={{
                          bgcolor: 'rgba(255, 255, 255, 0.2)',
                          width: 56,
                          height: 56,
                          mr: 3,
                          backdropFilter: 'blur(10px)',
                        }}
                      >
                        <BusinessIcon sx={{ fontSize: 28, color: 'white' }} />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                          🏢 {company.name}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Company Code: {company.code}
                        </Typography>
                      </Box>
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ opacity: 0.8 }}>
                            🌍 Country:
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {company.countryCode}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ opacity: 0.8 }}>
                            💰 Currency:
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {company.currency}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Subscription Info Card */}
            {isCompanyAdmin && subscription && (
              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    borderRadius: 4,
                    background:
                      subscription.status === 'past_due'
                        ? 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)'
                        : subscription.status === 'trial' && getTrialDaysLeft() <= 3
                        ? 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)'
                        : subscription.status === 'trial'
                        ? 'linear-gradient(135deg, #ffa726 0%, #fb8c00 100%)'
                        : 'linear-gradient(135deg, #66bb6a 0%, #43a047 100%)',
                    color: 'white',
                    boxShadow:
                      subscription.status === 'past_due'
                        ? '0 8px 32px rgba(244, 67, 54, 0.3)'
                        : subscription.status === 'trial' && getTrialDaysLeft() <= 3
                        ? '0 8px 32px rgba(255, 107, 107, 0.3)'
                        : subscription.status === 'trial'
                        ? '0 8px 32px rgba(255, 167, 38, 0.3)'
                        : '0 8px 32px rgba(102, 187, 106, 0.3)',
                    border: 'none',
                    position: 'relative',
                    overflow: 'hidden',
                    ...(subscription.status === 'past_due' && {
                      animation: 'pulse 2s infinite',
                      '@keyframes pulse': {
                        '0%': { transform: 'scale(1)' },
                        '50%': { transform: 'scale(1.02)' },
                        '100%': { transform: 'scale(1)' },
                      },
                    }),
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: '150px',
                      height: '150px',
                      background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)',
                      borderRadius: '50%',
                      transform: 'translate(50px, -50px)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 4, position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar
                          sx={{
                            bgcolor: 'rgba(255, 255, 255, 0.2)',
                            width: 56,
                            height: 56,
                            mr: 3,
                            backdropFilter: 'blur(10px)',
                          }}
                        >
                          <CreditCardIcon sx={{ fontSize: 28, color: 'white' }} />
                        </Avatar>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                            📋 {subscription.planId?.name || 'Starter'} Plan
                          </Typography>
                          <Chip
                            label={subscription.status}
                            size="small"
                            sx={{
                              fontWeight: 600,
                              borderRadius: 2,
                              backgroundColor: 'rgba(255, 255, 255, 0.2)',
                              color: 'white',
                              border: '1px solid rgba(255, 255, 255, 0.3)',
                            }}
                          />
                        </Box>
                      </Box>

                      {(subscription.status === 'trial' || subscription.status === 'expired') && (
                        <Button
                          variant="contained"
                          startIcon={<UpgradeIcon />}
                          onClick={handleUpgradeClick}
                          sx={{
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            color: 'white',
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                            borderRadius: 2,
                            fontWeight: 600,
                            textTransform: 'none',
                            backdropFilter: 'blur(10px)',
                            '&:hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.3)',
                              transform: 'translateY(-2px)',
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            },
                            transition: 'all 0.3s ease-in-out',
                          }}
                        >
                          {subscription.status === 'trial' ? '🚀 Upgrade' : '🔄 Renew'}
                        </Button>
                      )}
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ opacity: 0.8 }}>
                            � Price:
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {company?.currency || 'USD'} {subscription.price}/{subscription.billingCycle === 'yearly' ? 'year' : 'month'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        {subscription.trialEndDate && subscription.status === 'trial' ? (
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            gap: 1, 
                            backgroundColor: 'rgba(255, 255, 255, 0.15)',
                            padding: 2,
                            borderRadius: 2,
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            backdropFilter: 'blur(10px)'
                          }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                              <Typography variant="body2" sx={{ 
                                opacity: 0.9, 
                                fontWeight: 500,
                                textAlign: 'center'
                              }}>
                                ⏰ Trial Period
                              </Typography>
                            </Box>
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="h6" sx={{ 
                                fontWeight: 700, 
                                fontSize: '1.1rem',
                                color: getTrialDaysLeft() <= 3 ? '#ffeb3b' : 'white',
                                mb: 0.5
                              }}>
                                {getTrialDaysLeft()} Days Remaining
                              </Typography>
                              <Typography variant="body2" sx={{ 
                                fontWeight: 500, 
                                opacity: 0.9,
                                fontSize: '0.875rem'
                              }}>
                                Expires on {formatDate(subscription.trialEndDate)}
                              </Typography>
                            </Box>
                          </Box>
                        ) : subscription.nextBillingDate && subscription.status === 'active' ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ opacity: 0.8 }}>
                              📅 Next bill:
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100px' }} title={formatDate(subscription.nextBillingDate)}>
                              {formatDate(subscription.nextBillingDate)}
                            </Typography>
                          </Box>
                        ) : null}
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </Container>

        {/* Trial Expiring Warning - Only show if trial is critically low */}
        {console.log('Rendering trial warning check:', { isCompanyAdmin, subscriptionStatus: subscription?.status, trialDaysLeft: getTrialDaysLeft() })}
        {isCompanyAdmin && subscription?.status === 'trial' && getTrialDaysLeft() <= 1 && (
          <Container maxWidth="lg" sx={{ px: 3, mb: 3 }}>
            <Alert
              severity="error"
              sx={{
                borderRadius: 4,
                background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
                color: 'white',
                '& .MuiAlert-icon': { color: 'white' },
                boxShadow: '0 8px 32px rgba(255, 107, 107, 0.3)',
                border: 'none',
                animation: 'pulse 2s infinite',
                '@keyframes pulse': {
                  '0%': { transform: 'scale(1)', boxShadow: '0 8px 32px rgba(255, 107, 107, 0.3)' },
                  '50%': { transform: 'scale(1.02)', boxShadow: '0 12px 40px rgba(255, 107, 107, 0.4)' },
                  '100%': { transform: 'scale(1)', boxShadow: '0 8px 32px rgba(255, 107, 107, 0.3)' },
                },
              }}
              icon={<CelebrationIcon />}
              action={
                <Button
                  color="inherit"
                  variant="contained"
                  startIcon={<UpgradeIcon />}
                  onClick={handleUpgradeClick}
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    borderRadius: 2,
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.3)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease-in-out',
                  }}
                >
                  🚨 UPGRADE NOW
                </Button>
              }
            >
              <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                ⚠️ CRITICAL: Your trial expires in {getTrialDaysLeft()} days! Don't lose access to your data.
              </Typography>
            </Alert>
          </Container>
        )}

        <Container maxWidth="lg" sx={{ px: 3, pb: 4 }}>
          {/* Enhanced Stats Cards - Admin Only */}
          {isCompanyAdmin && (
            <Grid container spacing={3} sx={{ mb: 6 }}>
                {stats.map((stat, index) => (
                  <Grid item xs={12} sm={6} lg={3} key={index}>
                  <Card
                    sx={{
                      borderRadius: 4,
                      background: 'white',
                      border: '1px solid #e2e8f0',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      transition: 'all 0.3s ease-in-out',
                      cursor: 'pointer',
                      position: 'relative',
                      overflow: 'hidden',
                      minHeight: '200px',
                      display: 'flex',
                      flexDirection: 'column',
                      '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                      borderColor: stat.color,
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: '120px',
                      height: '120px',
                      background: `radial-gradient(circle, ${stat.color}08 0%, transparent 70%)`,
                      borderRadius: '50%',
                      transform: 'translate(40px, -40px)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 4, position: 'relative', zIndex: 1, flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                      <Avatar
                        sx={{
                          bgcolor: stat.color,
                          width: 56,
                          height: 56,
                          boxShadow: `0 10px 15px -3px ${stat.color}30`,
                        }}
                      >
                        <stat.icon sx={{ fontSize: 28, color: 'white' }} />
                      </Avatar>
                      <Box sx={{ textAlign: 'right', minWidth: 0, flex: 1 }}>
                        <Typography
                          variant="h4"
                          sx={{
                            fontWeight: 800,
                            color: '#1a202c',
                            fontSize: stat.value && typeof stat.value === 'string' && stat.value.includes('/') ? '1.5rem' : '2rem',
                            lineHeight: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '100%',
                          }}
                          title={stat.value} // Show full value on hover
                        >
                          {stat.value}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: '#2d3748',
                          mb: 1,
                          fontSize: '1.1rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={stat.title} // Show full title on hover
                      >
                        {stat.title}
                      </Typography>

                    <Chip
                      label={stat.change}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        borderRadius: 2,
                        maxWidth: '100%',
                        '& .MuiChip-label': {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        },
                        backgroundColor:
                          stat.change.startsWith('+') || stat.change === 'Active' || stat.change === 'New company'
                            ? '#c6f6d5'
                            : stat.change === 'days remaining' || stat.change === 'Next payment'
                            ? '#fed7d7'
                            : '#e2e8f0',
                        color:
                          stat.change.startsWith('+') || stat.change === 'Active' || stat.change === 'New company'
                            ? '#22543d'
                            : stat.change === 'days remaining' || stat.change === 'Next payment'
                            ? '#c53030'
                            : '#4a5568',
                        border: `1px solid ${
                          stat.change.startsWith('+') || stat.change === 'Active' || stat.change === 'New company'
                            ? '#9ae6b4'
                            : stat.change === 'days remaining' || stat.change === 'Next payment'
                            ? '#feb2b2'
                            : '#cbd5e0'
                        }`,
                      }}
                    />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          )}

          {/* Storage Monitoring Widget - Admin Only */}
          {isCompanyAdmin && (
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} lg={6}>
                <StorageMonitoringWidget onUpgradeClick={handleUpgradeClick} />
              </Grid>
            </Grid>
          )}

        {/* Main Content Grid - Admin Only */}
        {isCompanyAdmin && (
          <Grid container spacing={3}>
            {/* Recent Activities */}
            <Grid item xs={12} md={6}>
            <Paper
              sx={{
                p: 3,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                border: '1px solid #e0e4e7',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <Typography
                variant="h6"
                component="h2"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  color: '#1a1a1a',
                  mb: 3,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <SettingsIcon sx={{ color: '#1976d2' }} />
                Setup Guide
              </Typography>
              <Box
                sx={{
                  maxHeight: '500px',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  pr: 1,
                  '&::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: 'linear-gradient(135deg, #cbd5e0 0%, #a0aec0 100%)',
                    borderRadius: '3px',
                    transition: 'all 0.3s ease',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    background: 'linear-gradient(135deg, #a0aec0 0%, #718096 100%)',
                  },
                  '&::-webkit-scrollbar-corner': {
                    background: 'transparent',
                  },
                  // Firefox scrollbar styling
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#cbd5e0 transparent',
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: 1 }}>
                  {setupGuides.map((guide) => (
                    <Card
                      key={guide.id}
                      sx={{
                        borderRadius: 2,
                        border: '1px solid #e2e8f0',
                        transition: 'all 0.3s ease-in-out',
                        cursor: 'pointer',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                          borderColor: guide.color,
                        },
                      }}
                      onClick={() => window.location.href = guide.path}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: 2,
                              backgroundColor: `${guide.color}15`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            <Typography sx={{ fontSize: '1.5rem' }}>
                              {guide.title.split(' ')[0]}
                            </Typography>
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 600,
                                color: '#1a202c',
                                mb: 1,
                                fontSize: '1rem',
                                lineHeight: 1.3,
                              }}
                            >
                              {guide.title}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#4a5568',
                                mb: 2,
                                lineHeight: 1.5,
                                fontSize: '0.875rem',
                              }}
                            >
                              {guide.description}
                            </Typography>
                            <Box sx={{ mb: 2 }}>
                              {guide.steps.map((step, index) => (
                                <Typography
                                  key={index}
                                  variant="caption"
                                  sx={{
                                    color: '#718096',
                                    display: 'block',
                                    fontSize: '0.75rem',
                                    lineHeight: 1.4,
                                    mb: 0.5,
                                    pl: 1.5,
                                    position: 'relative',
                                    '&::before': {
                                      content: '"•"',
                                      position: 'absolute',
                                      left: 0,
                                      color: guide.color,
                                      fontWeight: 'bold',
                                    },
                                  }}
                                >
                                  {step}
                                </Typography>
                              ))}
                            </Box>
                            <Button
                              size="small"
                              variant="outlined"
                              sx={{
                                borderColor: guide.color,
                                color: guide.color,
                                '&:hover': {
                                  borderColor: guide.color,
                                  backgroundColor: `${guide.color}10`,
                                },
                                fontSize: '0.75rem',
                                py: 0.5,
                                px: 2,
                                minWidth: 'auto',
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                window.location.href = guide.path;
                              }}
                            >
                              Get Started
                            </Button>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Upcoming Events */}
          <Grid item xs={12} md={6}>
            <Paper
              sx={{
                p: 3,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                border: '1px solid #e0e4e7',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <Typography
                variant="h6"
                component="h2"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  color: '#1a1a1a',
                  mb: 3,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <EventAvailableIcon sx={{ color: '#2e7d32' }} />
                Upcoming Tasks
                <Chip
                  label={`${upcomingEvents.length} tasks`}
                  size="small"
                  sx={{
                    ml: 'auto',
                    fontSize: '0.7rem',
                    height: '20px',
                    backgroundColor: '#e8f5e8',
                    color: '#2e7d32',
                    fontWeight: 600,
                  }}
                />
              </Typography>
              <Box
                sx={{
                  maxHeight: '400px',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  pr: 1,
                  '&::-webkit-scrollbar': {
                    width: '4px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: 'linear-gradient(135deg, #a0aec0 0%, #718096 100%)',
                    borderRadius: '2px',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    background: 'linear-gradient(135deg, #718096 0%, #4a5568 100%)',
                  },
                }}
              >
                <List sx={{ p: 0 }}>
                  {upcomingEvents.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" sx={{ color: '#666', fontStyle: 'italic' }}>
                        🎉 All caught up! No pending tasks.
                      </Typography>
                    </Box>
                  ) : (
                    upcomingEvents.map((event) => (
                      <ListItem
                        key={event.id}
                        sx={{
                          borderRadius: 2,
                          mb: 1.5,
                          backgroundColor: 'rgba(255, 255, 255, 0.8)',
                          border: '1px solid rgba(0, 0, 0, 0.05)',
                          transition: 'all 0.2s ease-in-out',
                          cursor: event.action ? 'pointer' : 'default',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                            transform: event.action ? 'translateX(4px)' : 'none',
                            boxShadow: event.action ? '0 4px 12px rgba(0, 0, 0, 0.1)' : 'none',
                          },
                        }}
                        onClick={event.action}
                      >
                        <ListItemAvatar sx={{ minWidth: 'auto', mr: 2 }}>
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              backgroundColor:
                                event.type === 'urgent' ? '#ffebee' :
                                event.type === 'setup' ? '#e3f2fd' :
                                event.type === 'onboarding' ? '#f3e5f5' :
                                event.type === 'subscription' ? '#e8f5e8' :
                                event.type === 'payroll' ? '#fff3e0' : '#f5f5f5',
                              color:
                                event.type === 'urgent' ? '#d32f2f' :
                                event.type === 'setup' ? '#1976d2' :
                                event.type === 'onboarding' ? '#7b1fa2' :
                                event.type === 'subscription' ? '#2e7d32' :
                                event.type === 'payroll' ? '#f57c00' : '#666',
                            }}
                          >
                            {event.icon ||
                             (event.type === 'urgent' ? '⚠️' :
                              event.type === 'setup' ? '⚙️' :
                              event.type === 'onboarding' ? '👥' :
                              event.type === 'subscription' ? '💳' :
                              event.type === 'payroll' ? '💰' : '📋')}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a1a1a', flex: 1 }}>
                                {event.title}
                              </Typography>
                              {event.priority <= 3 && (
                                <Chip
                                  label="High Priority"
                                  size="small"
                                  sx={{
                                    fontSize: '0.6rem',
                                    height: '16px',
                                    backgroundColor: '#ffebee',
                                    color: '#d32f2f',
                                    fontWeight: 600,
                                  }}
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: '#555',
                                  fontWeight: 500,
                                  display: 'block',
                                  mb: 0.5
                                }}
                              >
                                {event.date}
                              </Typography>
                              {event.description && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: '#777',
                                    fontSize: '0.7rem',
                                    lineHeight: 1.3,
                                    display: 'block'
                                  }}
                                >
                                  {event.description}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                        <Chip
                          label={event.type}
                          size="small"
                          sx={{
                            ml: 1,
                            fontWeight: 600,
                            borderRadius: 1,
                            fontSize: '0.65rem',
                            height: '18px',
                            backgroundColor:
                              event.type === 'urgent' ? '#ffebee' :
                              event.type === 'setup' ? '#e3f2fd' :
                              event.type === 'onboarding' ? '#f3e5f5' :
                              event.type === 'subscription' ? '#e8f5e8' :
                              event.type === 'payroll' ? '#fff3e0' : '#f5f5f5',
                            color:
                              event.type === 'urgent' ? '#d32f2f' :
                              event.type === 'setup' ? '#1976d2' :
                              event.type === 'onboarding' ? '#7b1fa2' :
                              event.type === 'subscription' ? '#2e7d32' :
                              event.type === 'payroll' ? '#f57c00' : '#666',
                            border: `1px solid ${
                              event.type === 'urgent' ? '#ffcdd2' :
                              event.type === 'setup' ? '#bbdefb' :
                              event.type === 'onboarding' ? '#ce93d8' :
                              event.type === 'subscription' ? '#c8e6c9' :
                              event.type === 'payroll' ? '#ffcc02' : '#e0e0e0'
                            }`,
                          }}
                        />
                      </ListItem>
                    ))
                  )}
                </List>
              </Box>
            </Paper>
          </Grid>
        </Grid>
        )}
        </Container>

        {/* Upgrade Plan Dialog - Admin Only */}
        {isCompanyAdmin && (
          <>
            {console.log('Rendering upgrade dialog:', { upgradeDialogOpen, plans: plans?.length, selectedPlan })}
            <Dialog
              open={upgradeDialogOpen}
              onClose={handleUpgradeCancel}
              maxWidth="md"
              fullWidth
              sx={{
                '& .MuiDialog-paper': {
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
                  maxHeight: '90vh',
                  overflow: 'auto',
                },
              }}
            >
              <DialogTitle
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  borderRadius: '3px 3px 0 0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontWeight: 700,
                  py: 2,
                  px: 3,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <UpgradeIcon sx={{ fontSize: 24 }} />
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Upgrade Your Plan
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Step {currentUpgradeStep} of 2
                </Typography>
              </DialogTitle>

              <DialogContent sx={{ p: 0 }}>
                {/* Step Indicator */}
                <Box sx={{ p: 2, pb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                    {[1, 2].map((step, index) => (
                      <React.Fragment key={step}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: currentUpgradeStep >= step 
                              ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                              : '#e2e8f0',
                            color: currentUpgradeStep >= step ? 'white' : '#64748b',
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            transition: 'all 0.3s ease',
                          }}
                        >
                          {step}
                        </Box>
                        {index < 1 && (
                          <Box
                            sx={{
                              width: 40,
                              height: 2,
                              background: currentUpgradeStep > step ? '#667eea' : '#e2e8f0',
                              mx: 1.5,
                              transition: 'all 0.3s ease',
                            }}
                          />
                        )}
                      </React.Fragment>
                    ))}
                  </Box>
                </Box>

                {/* Step Content */}
                {currentUpgradeStep === 1 && (
                  <Box sx={{ px: 3, pb: 3 }}>
                    <Typography variant="h6" sx={{ textAlign: 'center', mb: 1.5, fontWeight: 600 }}>
                      Choose Your Plan
                    </Typography>
                    <Typography variant="body2" sx={{ textAlign: 'center', mb: 3, color: '#666' }}>
                      Select the perfect plan for your business needs
                    </Typography>

                    {/* Billing Cycle Toggle */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2.5 }}>
                      <Box sx={{ 
                        backgroundColor: '#f1f5f9', 
                        p: 0.5, 
                        borderRadius: 2.5,
                        display: 'flex'
                      }}>
                        <Button
                          onClick={() => handleUpgradeDataUpdate({ billingCycle: 'monthly' })}
                          sx={{
                            px: 3,
                            py: 1.5,
                            borderRadius: 2.5,
                            fontWeight: 600,
                            textTransform: 'none',
                            minWidth: 100,
                            transition: 'all 0.2s ease',
                            ...(upgradeData.billingCycle === 'monthly' ? {
                              backgroundColor: 'white',
                              color: '#1a202c',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            } : {
                              backgroundColor: 'transparent',
                              color: '#64748b',
                              '&:hover': { 
                                backgroundColor: 'rgba(255,255,255,0.5)',
                                color: '#1a202c'
                              }
                            })
                          }}
                        >
                          Monthly
                        </Button>
                        <Button
                          onClick={() => handleUpgradeDataUpdate({ billingCycle: 'yearly' })}
                          sx={{
                            px: 3,
                            py: 1.5,
                            borderRadius: 2.5,
                            fontWeight: 600,
                            textTransform: 'none',
                            minWidth: 100,
                            transition: 'all 0.2s ease',
                            ...(upgradeData.billingCycle === 'yearly' ? {
                              backgroundColor: 'white',
                              color: '#1a202c',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            } : {
                              backgroundColor: 'transparent',
                              color: '#64748b',
                              '&:hover': { 
                                backgroundColor: 'rgba(255,255,255,0.5)',
                                color: '#1a202c'
                              }
                            })
                          }}
                        >
                          Yearly
                        </Button>
                      </Box>
                    </Box>

                    {plansLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <Typography sx={{ fontWeight: 500, color: '#333' }}>Loading plans...</Typography>
                      </Box>
                    ) : plans.length > 0 ? (
                      <Grid container spacing={2}>
                        {plans.map((plan) => {
                          const isSelected = upgradeData.planId === plan._id;
                          const price = plan.price[upgradeData.billingCycle];
                          
                          // Format price same as onboarding - monthly equivalent for yearly, direct price for monthly
                          const displayPrice = upgradeData.billingCycle === 'yearly' 
                            ? (price / 12).toFixed(0) 
                            : price.toFixed(0);
                          
                          // Format yearly price with proper decimals
                          const yearlyPrice = plan.price.yearly?.toFixed(2);
                          
                          return (
                            <Grid item xs={12} sm={6} md={4} key={plan._id}>
                              <Card
                                onClick={() => handleUpgradeDataUpdate({ planId: plan._id })}
                                sx={{
                                  position: 'relative',
                                  border: isSelected ? '2px solid #667eea' : '2px solid #e2e8f0',
                                  cursor: 'pointer',
                                  borderRadius: 4,
                                  transition: 'all 0.3s ease-in-out',
                                  background: isSelected
                                    ? 'linear-gradient(135deg, #f8f9ff 0%, #e8f2ff 100%)'
                                    : 'white',
                                  transform: isSelected ? 'scale(1.05)' : 'scale(1)',
                                  '&:hover': {
                                    transform: 'scale(1.05)',
                                    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
                                    borderColor: '#667eea',
                                  },
                                  boxShadow: isSelected 
                                    ? '0 12px 40px rgba(102, 126, 234, 0.2)' 
                                    : '0 4px 20px rgba(0, 0, 0, 0.08)',
                                }}
                              >
                                {/* Popular Badge */}
                                {plan.isPopular && (
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      top: -16,
                                      left: '50%',
                                      transform: 'translateX(-50%)',
                                      zIndex: 1,
                                    }}
                                  >
                                    <Chip
                                      label="⭐ Most Popular"
                                      sx={{
                                        background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)',
                                        color: 'white',
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                        height: 28,
                                      }}
                                    />
                                  </Box>
                                )}

                                {/* Selection Indicator */}
                                {isSelected && (
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      top: 16,
                                      right: 16,
                                      width: 24,
                                      height: 24,
                                      borderRadius: '50%',
                                      background: '#667eea',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                  >
                                    <CheckCircleIcon sx={{ width: 16, height: 16, color: 'white' }} />
                                  </Box>
                                )}

                                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                                  {/* Plan Name & Description */}
                                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: '#1a202c' }}>
                                    {plan.name}
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: '#64748b', mb: 4, fontSize: '0.875rem' }}>
                                    {plan.description}
                                  </Typography>

                                  {/* Pricing Section */}
                                  <Box sx={{ mb: 4 }}>
                                    {/* Original Price & Discount */}
                                    {plan.originalPrice && plan.discountPercentage > 0 && (
                                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                                        <Typography
                                          variant="body1"
                                          sx={{
                                            textDecoration: 'line-through',
                                            color: '#9ca3af',
                                            mr: 2,
                                          }}
                                        >
                                          {getCurrencySymbol(plan.currency || company?.currency || 'USD')}{plan.originalPrice}
                                        </Typography>
                                        <Chip
                                          label={`${plan.discountPercentage}% OFF`}
                                          size="small"
                                          sx={{
                                            backgroundColor: '#10b981',
                                            color: 'white',
                                            fontWeight: 600,
                                            fontSize: '0.75rem',
                                          }}
                                        />
                                      </Box>
                                    )}

                                    {/* Current Price */}
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', mb: 2 }}>
                                      <Typography variant="h3" sx={{ fontWeight: 800, color: '#1a202c' }}>
                                        {getCurrencySymbol(plan.currency || company?.currency || 'USD')}{displayPrice}
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: '#64748b', ml: 1 }}>
                                        /{upgradeData.billingCycle === 'yearly' ? 'mo' : 'month'}
                                      </Typography>
                                    </Box>

                                    {/* Yearly Billing Note */}
                                    {upgradeData.billingCycle === 'yearly' && (
                                      <Typography variant="body2" sx={{ color: '#10b981', fontWeight: 600 }}>
                                        Billed {getCurrencySymbol(plan.currency || company?.currency || 'USD')}{yearlyPrice} annually
                                      </Typography>
                                    )}
                                  </Box>

                                  {/* Trial Period */}
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 4 }}>
                                    <AccessTimeIcon sx={{ width: 16, height: 16, color: '#64748b', mr: 1 }} />
                                    <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.875rem' }}>
                                      {plan.trialDays} days free trial
                                    </Typography>
                                  </Box>

                                  {/* Features */}
                                  <Box sx={{ textAlign: 'left' }}>
                                    {/* Employee Limit */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                      <PeopleIcon sx={{ width: 18, height: 18, color: '#667eea', mr: 2 }} />
                                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                        Up to {plan.features.maxEmployees} employees
                                      </Typography>
                                    </Box>

                                    {/* Branches Limit */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                      <BusinessIcon sx={{ width: 18, height: 18, color: '#667eea', mr: 2 }} />
                                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                        Up to {plan.features.maxBranches} branches
                                      </Typography>
                                    </Box>

                                    {/* Storage */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                      <CloudIcon sx={{ width: 18, height: 18, color: '#667eea', mr: 2 }} />
                                      <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                        {plan.features.storage}GB storage
                                      </Typography>
                                    </Box>

                                    {/* All Modules - Show all features without limiting */}
                                    {plan.features.modules && plan.features.modules.map((module, index) => (
                                      <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                        <CheckCircleIcon sx={{ width: 18, height: 18, color: '#10b981', mr: 2 }} />
                                        <Typography variant="body2" sx={{ fontSize: '0.875rem', textTransform: 'capitalize' }}>
                                          {module}
                                        </Typography>
                                      </Box>
                                    ))}
                                  </Box>
                                </CardContent>
                              </Card>
                            </Grid>
                          );
                        })}
                      </Grid>
                    ) : (
                      <Typography sx={{ textAlign: 'center', py: 8, color: '#666' }}>
                        No plans available for your region.
                      </Typography>
                    )}

                    {/* Plan Comparison Section */}
                    {plans.length > 0 && (
                      <Box sx={{
                        mt: 6,
                        p: 4,
                        backgroundColor: '#f8fafc',
                        borderRadius: 3,
                      }}>
                        <Typography variant="h6" sx={{ textAlign: 'center', mb: 4, fontWeight: 600, color: '#1a202c' }}>
                          Why Choose Our Plans?
                        </Typography>
                        <Grid container spacing={4}>
                          <Grid item xs={12} md={4}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Box sx={{
                                width: 48,
                                height: 48,
                                backgroundColor: '#e0f2fe',
                                borderRadius: 3,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: 'auto',
                                mb: 2,
                              }}>
                                <PeopleIcon sx={{ width: 24, height: 24, color: '#0284c7' }} />
                              </Box>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: '#1a202c' }}>
                                Scalable
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.875rem' }}>
                                Grow your business with plans that scale with you
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Box sx={{
                                width: 48,
                                height: 48,
                                backgroundColor: '#dcfce7',
                                borderRadius: 3,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: 'auto',
                                mb: 2,
                              }}>
                                <CheckCircleIcon sx={{ width: 24, height: 24, color: '#16a34a' }} />
                              </Box>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: '#1a202c' }}>
                                Feature-Rich
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.875rem' }}>
                                All essential HR features in one comprehensive platform
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Box sx={{ textAlign: 'center' }}>
                              <Box sx={{
                                width: 48,
                                height: 48,
                                backgroundColor: '#f3e8ff',
                                borderRadius: 3,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: 'auto',
                                mb: 2,
                              }}>
                                <AccessTimeIcon sx={{ width: 24, height: 24, color: '#9333ea' }} />
                              </Box>
                              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: '#1a202c' }}>
                                Free Trial
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.875rem' }}>
                                Try before you buy with our generous trial period
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    )}

                    {upgradeErrors.planId && (
                      <Typography sx={{ color: 'error.main', mt: 2, textAlign: 'center' }}>
                        {upgradeErrors.planId}
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Step 2: Payment Information */}
                {currentUpgradeStep === 2 && (
                  <Box sx={{ px: 3, pb: 3 }}>
                    <Typography variant="h6" sx={{ textAlign: 'center', mb: 1.5, fontWeight: 600 }}>
                      Payment Information
                    </Typography>
                    <Typography variant="body2" sx={{ textAlign: 'center', mb: 2.5, color: '#666' }}>
                      Complete your payment details to upgrade your plan
                    </Typography>

                    {/* Plan Summary */}
                    {upgradeData.planId && (
                      <Box sx={{ 
                        mb: 2.5, 
                        p: 2, 
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                        borderRadius: 2, 
                        color: 'white' 
                      }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.25 }}>
                              {plans.find(p => p._id === upgradeData.planId)?.name} Plan
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.875rem' }}>
                              {upgradeData.billingCycle === 'yearly' ? 'Annual' : 'Monthly'} Subscription
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                              {getCurrencySymbol(company?.currency || 'USD')}
                              {plans.find(p => p._id === upgradeData.planId)?.price[upgradeData.billingCycle]?.toFixed(2)}
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.875rem' }}>
                              per {upgradeData.billingCycle === 'yearly' ? 'year' : 'month'}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    )}

                    {/* Payment Method Selection */}
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                        Payment Method
                      </Typography>
                      <Grid container spacing={2}>
                        {[
                          { id: 'bank_transfer', label: 'Bank Transfer', icon: '🏦' },
                          { id: 'upi', label: 'UPI Payment', icon: '📱' }
                        ].map((method) => (
                          <Grid item xs={12} sm={6} key={method.id}>
                            <Card
                              onClick={() => handleUpgradeDataUpdate({ paymentMethod: method.id })}
                              sx={{
                                cursor: 'pointer',
                                border: upgradeData.paymentMethod === method.id ? '2px solid #667eea' : '1px solid #e2e8f0',
                                background: upgradeData.paymentMethod === method.id ? '#f8f9ff' : 'white',
                                '&:hover': { borderColor: '#667eea' },
                                transition: 'all 0.2s ease',
                              }}
                            >
                              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                                <Typography variant="h3" sx={{ mb: 0.5 }}>{method.icon}</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {method.label}
                                </Typography>
                                {upgradeData.paymentMethod === method.id && (
                                  <CheckCircleIcon sx={{ color: '#667eea', mt: 0.5 }} />
                                )}
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                      {upgradeErrors.paymentMethod && (
                        <Typography sx={{ color: 'error.main', mt: 1, fontSize: '0.875rem' }}>
                          {upgradeErrors.paymentMethod}
                        </Typography>
                      )}
                    </Box>

                    {/* Payment Details */}
                    {upgradeData.paymentMethod === 'bank_transfer' && (
                      <Box sx={{ mb: 2.5, p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, display: 'flex', alignItems: 'center' }}>
                          <PaymentIcon sx={{ mr: 1, fontSize: 20 }} />
                          Bank Transfer Details
                        </Typography>
                        
                        {bankLoading ? (
                          <Typography variant="body2">Loading bank details...</Typography>
                        ) : (
                          <Grid container spacing={1.5}>
                            <Grid item xs={12} sm={6}>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, fontSize: '0.875rem' }}>
                                  Bank Name
                                </Typography>
                                <Box sx={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between', 
                                  alignItems: 'center',
                                  backgroundColor: 'white', 
                                  p: 1.5, 
                                  borderRadius: 1, 
                                  border: '1px solid #e0e0e0' 
                                }}>
                                  <Typography sx={{ fontWeight: 500 }}>
                                    {bankDetails.bankName || 'Not configured'}
                                  </Typography>
                                  {bankDetails.bankName && (
                                    <Button
                                      size="small"
                                      onClick={() => copyToClipboard(bankDetails.bankName)}
                                      sx={{ minWidth: 'auto', p: 0.5 }}
                                    >
                                      📋
                                    </Button>
                                  )}
                                </Box>
                              </Box>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                  Account Name
                                </Typography>
                                <Box sx={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between', 
                                  alignItems: 'center',
                                  backgroundColor: 'white', 
                                  p: 2, 
                                  borderRadius: 1, 
                                  border: '1px solid #e0e0e0' 
                                }}>
                                  <Typography sx={{ fontWeight: 500 }}>
                                    {bankDetails.accountName || 'Not configured'}
                                  </Typography>
                                  {bankDetails.accountName && (
                                    <Button
                                      size="small"
                                      onClick={() => copyToClipboard(bankDetails.accountName)}
                                      sx={{ minWidth: 'auto', p: 0.5 }}
                                    >
                                      📋
                                    </Button>
                                  )}
                                </Box>
                              </Box>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                  Account Number
                                </Typography>
                                <Box sx={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between', 
                                  alignItems: 'center',
                                  backgroundColor: 'white', 
                                  p: 2, 
                                  borderRadius: 1, 
                                  border: '1px solid #e0e0e0' 
                                }}>
                                  <Typography sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                                    {bankDetails.accountNumber || 'Not configured'}
                                  </Typography>
                                  {bankDetails.accountNumber && (
                                    <Button
                                      size="small"
                                      onClick={() => copyToClipboard(bankDetails.accountNumber)}
                                      sx={{ minWidth: 'auto', p: 0.5 }}
                                    >
                                      📋
                                    </Button>
                                  )}
                                </Box>
                              </Box>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                  IFSC Code
                                </Typography>
                                <Box sx={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between', 
                                  alignItems: 'center',
                                  backgroundColor: 'white', 
                                  p: 2, 
                                  borderRadius: 1, 
                                  border: '1px solid #e0e0e0' 
                                }}>
                                  <Typography sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                                    {bankDetails.ifscCode || 'Not configured'}
                                  </Typography>
                                  {bankDetails.ifscCode && (
                                    <Button
                                      size="small"
                                      onClick={() => copyToClipboard(bankDetails.ifscCode)}
                                      sx={{ minWidth: 'auto', p: 0.5 }}
                                    >
                                      📋
                                    </Button>
                                  )}
                                </Box>
                              </Box>
                            </Grid>
                          </Grid>
                        )}
                      </Box>
                    )}

                    {upgradeData.paymentMethod === 'upi' && (
                      <Box sx={{ mb: 2.5, p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, display: 'flex', alignItems: 'center' }}>
                          <QrCodeIcon sx={{ mr: 1, fontSize: 20 }} />
                          UPI Payment Details
                        </Typography>
                        
                        {bankLoading ? (
                          <Typography variant="body2">Loading UPI details...</Typography>
                        ) : (
                          <Grid container spacing={1.5}>
                            <Grid item xs={12} sm={6}>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, fontSize: '0.875rem' }}>
                                  UPI ID
                                </Typography>
                                <Box sx={{ 
                                  display: 'flex', 
                                  justifyContent: 'space-between', 
                                  alignItems: 'center',
                                  backgroundColor: 'white', 
                                  p: 1.5, 
                                  borderRadius: 1, 
                                  border: '1px solid #e0e0e0' 
                                }}>
                                  <Typography sx={{ fontWeight: 500, fontFamily: 'monospace' }}>
                                    {bankDetails.upiId || 'Not configured'}
                                  </Typography>
                                  {bankDetails.upiId && (
                                    <Button
                                      size="small"
                                      onClick={() => copyToClipboard(bankDetails.upiId)}
                                      sx={{ minWidth: 'auto', p: 0.5 }}
                                    >
                                      📋
                                    </Button>
                                  )}
                                </Box>
                              </Box>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                  QR Code
                                </Typography>
                                <Box sx={{ 
                                  backgroundColor: 'white', 
                                  p: 2, 
                                  borderRadius: 1, 
                                  border: '1px solid #e0e0e0',
                                  textAlign: 'center'
                                }}>
                                  {bankDetails.qrCodeImage ? (
                                    <Button
                                      onClick={() => setShowQRModal(true)}
                                      variant="contained"
                                      size="small"
                                      sx={{ textTransform: 'none' }}
                                    >
                                      View QR Code
                                    </Button>
                                  ) : (
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                      Not available
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </Grid>
                          </Grid>
                        )}
                      </Box>
                    )}

                    {/* Payment Method Selection */}
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 2 }}>
                        Payment Method
                      </Typography>
                      <Grid container spacing={2}>
                        {[
                          { id: 'bank_transfer', label: 'Bank Transfer', icon: '🏦' },
                          { id: 'upi', label: 'UPI Payment', icon: '📱' },
                          { id: 'card', label: 'Credit/Debit Card', icon: '💳' }
                        ].map((method) => (
                          <Grid item xs={12} sm={4} key={method.id}>
                            <Card
                              onClick={() => handleUpgradeDataUpdate({ paymentMethod: method.id })}
                              sx={{
                                cursor: 'pointer',
                                border: upgradeData.paymentMethod === method.id ? '2px solid #667eea' : '1px solid #e2e8f0',
                                background: upgradeData.paymentMethod === method.id ? '#f8f9ff' : 'white',
                                '&:hover': { borderColor: '#667eea' },
                                transition: 'all 0.2s ease',
                              }}
                            >
                              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                                <Typography variant="h4" sx={{ mb: 1 }}>{method.icon}</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {method.label}
                                </Typography>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                      {upgradeErrors.paymentMethod && (
                        <Typography sx={{ color: 'error.main', mt: 1, fontSize: '0.875rem' }}>
                          {upgradeErrors.paymentMethod}
                        </Typography>
                      )}
                    </Box>

                    {/* Payment Reference */}
                    <Box sx={{ mb: 2.5 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 1.5 }}>
                        Payment Reference Number *
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="Enter your payment reference/transaction ID"
                        value={upgradeData.paymentReference}
                        onChange={(e) => handleUpgradeDataUpdate({ paymentReference: e.target.value })}
                        error={!!upgradeErrors.paymentReference}
                        helperText={upgradeErrors.paymentReference || 'Please enter the transaction ID or reference number after making the payment'}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Box>

                    {/* File Upload */}
                    <Box sx={{ mb: 2.5 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 1.5 }}>
                        Payment Proof (Optional)
                      </Typography>
                      <Box
                        sx={{
                          border: '2px dashed #e2e8f0',
                          borderRadius: 2,
                          p: 2.5,
                          textAlign: 'center',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderColor: '#667eea',
                            backgroundColor: '#f8f9ff',
                          },
                        }}
                      >
                        <input
                          type="file"
                          multiple
                          accept=".jpg,.jpeg,.png,.pdf"
                          onChange={handleFileUpload}
                          style={{ display: 'none' }}
                          id="payment-file-upload"
                        />
                        <label htmlFor="payment-file-upload" style={{ cursor: 'pointer' }}>
                          <PaperClipIcon style={{ width: 48, height: 48, color: '#667eea', marginBottom: 16 }} />
                          <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                            Upload Payment Screenshots
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#64748b' }}>
                            PNG, JPG or PDF (Max 5MB each)
                          </Typography>
                        </label>
                      </Box>

                      {/* Uploaded Files */}
                      {upgradeData.paymentAttachments.length > 0 && (
                        <Box sx={{ mt: 3 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
                            Uploaded Files:
                          </Typography>
                          {upgradeData.paymentAttachments.map((file) => (
                            <Box
                              key={file.id}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                p: 2,
                                border: '1px solid #e2e8f0',
                                borderRadius: 1,
                                mb: 1,
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <DocumentTextIcon style={{ width: 20, height: 20, color: '#667eea' }} />
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {file.name}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                                    {formatFileSize(file.size)}
                                  </Typography>
                                </Box>
                              </Box>
                              <Button
                                size="small"
                                onClick={() => removeAttachment(file.id)}
                                sx={{ minWidth: 'auto', p: 1 }}
                              >
                                <TrashIcon style={{ width: 16, height: 16, color: '#ef4444' }} />
                              </Button>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>

                    {upgradeErrors.general && (
                      <Typography sx={{ color: 'error.main', textAlign: 'center', mb: 2 }}>
                        {upgradeErrors.general}
                      </Typography>
                    )}
                  </Box>
                )}
              </DialogContent>

              <DialogActions sx={{ p: 4, gap: 2 }}>
                <Button
                  onClick={handleUpgradeCancel}
                  sx={{
                    borderRadius: 2,
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    textTransform: 'none',
                    color: '#64748b',
                  }}
                >
                  Cancel
                </Button>

                {currentUpgradeStep > 1 && (
                  <Button
                    onClick={handleUpgradePrevious}
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      px: 4,
                      py: 1.5,
                      fontWeight: 600,
                      textTransform: 'none',
                      borderColor: '#667eea',
                      color: '#667eea',
                      '&:hover': {
                        borderColor: '#5a6fd8',
                        backgroundColor: '#f8f9ff',
                      },
                    }}
                  >
                    Previous
                  </Button>
                )}

                <Button
                  onClick={currentUpgradeStep === 2 ? handleUpgradeConfirm : handleUpgradeNext}
                  variant="contained"
                  disabled={isUpgrading}
                  sx={{
                    borderRadius: 2,
                    px: 4,
                    py: 1.5,
                    fontWeight: 700,
                    textTransform: 'none',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    boxShadow: '0 4px 20px rgba(102, 126, 234, 0.25)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 30px rgba(102, 126, 234, 0.3)',
                    },
                    transition: 'all 0.3s ease-in-out',
                  }}
                >
                  {isUpgrading ? '⏳ Processing...' : currentUpgradeStep === 2 ? '💳 Complete Upgrade' : 'Next Step →'}
                </Button>
              </DialogActions>
            </Dialog>

            {/* QR Code Modal */}
            {showQRModal && bankDetails.qrCodeImage && (
              <Dialog
                open={showQRModal}
                onClose={() => setShowQRModal(false)}
                maxWidth="sm"
                fullWidth
                sx={{
                  '& .MuiDialog-paper': {
                    borderRadius: 4,
                    p: 2,
                  },
                }}
              >
                <DialogTitle sx={{ textAlign: 'center', pb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    UPI QR Code
                  </Typography>
                </DialogTitle>
                <DialogContent sx={{ textAlign: 'center', px: 4, pb: 2 }}>
                  <Box sx={{ 
                    backgroundColor: '#f5f5f5', 
                    borderRadius: 2, 
                    p: 3, 
                    mb: 3,
                    display: 'inline-block'
                  }}>
                    <img
                      src={bankDetails.qrCodeImage}
                      alt="UPI QR Code"
                      style={{ 
                        width: '200px', 
                        height: '200px', 
                        objectFit: 'contain'
                      }}
                    />
                  </Box>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                    Scan this QR code with any UPI app to make payment
                  </Typography>
                  {bankDetails.upiId && (
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
                      UPI ID: {bankDetails.upiId}
                    </Typography>
                  )}
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
                  <Button
                    onClick={() => setShowQRModal(false)}
                    variant="contained"
                    sx={{ borderRadius: 2, px: 4 }}
                  >
                    Close
                  </Button>
                </DialogActions>
              </Dialog>
            )}
          </>
        )}
      </Box>
      )}
    </>
  );
};

export default Dashboard;
